import { Currency } from '@prisma/client'

/**
 * Exchange rates (relative to CAD as base currency)
 * In a production environment, these would be fetched from a real-time API like:
 * - Bank of Canada API
 * - European Central Bank API
 * - Exchange Rate API
 * - Currency Layer API
 *
 * For now, using static rates updated monthly
 */
// Note: EUR and GBP not supported in current Prisma Currency enum
const EXCHANGE_RATES: Record<string, number> = {
  CAD: 1.0,      // Base currency
  USD: 0.74,     // 1 CAD = 0.74 USD (approximate)
  // EUR: 0.68,     // 1 CAD = 0.68 EUR (approximate) - Not supported in Prisma Currency enum
  // GBP: 0.58,     // 1 CAD = 0.58 GBP (approximate) - Not supported in Prisma Currency enum
}

/**
 * Default currency for the system (CAD for Canadian business)
 */
export const DEFAULT_CURRENCY: Currency = 'CAD'

/**
 * Convert price from one currency to another
 * @param amount Amount to convert
 * @param fromCurrency Source currency
 * @param toCurrency Target currency
 * @returns Converted amount
 */
export function convertCurrency(
  amount: number,
  fromCurrency: Currency,
  toCurrency: Currency
): number {
  if (fromCurrency === toCurrency) {
    return amount
  }

  // Convert to base currency (CAD) first, then to target currency
  const fromRate = EXCHANGE_RATES[fromCurrency]
  const toRate = EXCHANGE_RATES[toCurrency]
  
  if (!fromRate || !toRate) {
    throw new Error(`Unsupported currency conversion: ${fromCurrency} to ${toCurrency}`)
  }
  
  const amountInCAD = fromCurrency === 'CAD' ? amount : amount / fromRate
  const convertedAmount = toCurrency === 'CAD' ? amountInCAD : amountInCAD * toRate

  return Math.round(convertedAmount * 100) / 100 // Round to 2 decimal places
}

/**
 * Get the current exchange rate between two currencies
 * @param fromCurrency Source currency
 * @param toCurrency Target currency
 * @returns Exchange rate
 */
export function getExchangeRate(fromCurrency: Currency, toCurrency: Currency): number {
  if (fromCurrency === toCurrency) {
    return 1.0
  }

  // Convert via CAD base
  const fromRate = EXCHANGE_RATES[fromCurrency]
  const toRate = EXCHANGE_RATES[toCurrency]
  
  if (!fromRate || !toRate) {
    throw new Error(`Unsupported currency for exchange rate: ${fromCurrency} to ${toCurrency}`)
  }

  return toRate / fromRate
}

/**
 * Format price with user's preferred currency
 * @param price Price to format
 * @param currency Currency to format in
 * @param locale Locale for formatting (default: en-CA)
 * @returns Formatted currency string
 */
export function formatCurrencyPrice(
  price: number,
  currency: Currency = DEFAULT_CURRENCY,
  locale: string = 'en-CA'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price)
}

/**
 * Get currency symbol for a given currency
 * @param currency Currency code
 * @returns Currency symbol
 */
export function getCurrencySymbol(currency: Currency): string {
  const symbols: Record<string, string> = {
    CAD: 'C$',
    USD: '$',
    // EUR: '€', // Not supported in Prisma Currency enum
    // GBP: '£'  // Not supported in Prisma Currency enum
  }
  return symbols[currency] || currency
}

/**
 * Interface for currency conversion service
 */
export interface CurrencyConversionService {
  convert: (amount: number, from: Currency, to: Currency) => number
  format: (amount: number, currency: Currency, locale?: string) => string
  getRate: (from: Currency, to: Currency) => number
  getSymbol: (currency: Currency) => string
}

/**
 * Currency conversion service instance
 */
export const currencyService: CurrencyConversionService = {
  convert: convertCurrency,
  format: formatCurrencyPrice,
  getRate: getExchangeRate,
  getSymbol: getCurrencySymbol
}
